import React, { useState, useMemo } from 'react';
import electricIconSelected from '../../../images/hometool-electric-icon-selected.png';
import propaneIconSelected from '../../../images/hometool-propane-icon-selected.png';
import electricIconDeselected from '../../../images/hometool-electric-icon-deselected.png';
import propaneIconDeselected from '../../../images/hometool-propane-icon-deselected.png';
import houseIcon from '../../../images/hometool-house.png';
import cloudIcon from '../../../images/hometool-cloud.png';
import sunIcon from '../../../images/hometool-sun.png';
import groundIcon from '../../../images/hometool-ground.png';
import {
  calculateGeneratorSize,
  calculateMaxAllElectric,
  comparisonAppliancesList,
  electricAppliancesList,
} from '../utils/calculateGenerator';
import { pushHomeToolEvent } from '../utils/dataLayerEvents';
import EmailModal from '../components/EmailModal';

const initialComparisonSelections = {
  kitchenRange: null,
  clothesDryer: null,
  airFurnace: null,
  waterHeater: null,
};

const maxAllElectricKw = calculateMaxAllElectric();

const InteractiveHomeTool = () => {
  const [comparisonSelections, setComparisonSelections] = useState(initialComparisonSelections);
  const [electricSelections, setElectricSelections] = useState(new Set());
  const [isEmailModalOpen, setIsEmailModalOpen] = useState(false);
  const [validationMessage, setValidationMessage] = useState(null);

  const { propaneKw } = useMemo(
    () => calculateGeneratorSize(comparisonSelections, electricSelections),
    [comparisonSelections, electricSelections]
  );

  const validationState = useMemo(() => {
    const comparisonValues = Object.values(comparisonSelections);
    const hasAnyComparison = comparisonValues.some(v => v !== null);
    const hasAnyElectric = electricSelections.size > 0;
    const hasAnySelection = hasAnyComparison || hasAnyElectric;
    const hasPropaneSelected = comparisonValues.some(v => v === 'propane');

    return { hasAnySelection, hasPropaneSelected };
  }, [comparisonSelections, electricSelections]);

  const handleComparisonSelect = (key, fuelType) => {
    setComparisonSelections(prev => {
      const previousFuelType = prev[key];
      const nextFuelType = previousFuelType === fuelType ? null : fuelType;

      pushHomeToolEvent('home_tool_appliance_compare_select', {
        appliance_key: key,
        fuel_type: nextFuelType ?? 'none',
      });

      return {
        ...prev,
        [key]: nextFuelType,
      };
    });
  };

  const handleElectricToggle = (id) => {
    setElectricSelections(prev => {
      const next = new Set(prev);
      const isCurrentlySelected = next.has(id);

      if (isCurrentlySelected) {
        next.delete(id);
      } else {
        next.add(id);
      }

      pushHomeToolEvent('home_tool_electric_appliance_toggle', {
        appliance_key: id,
        fuel_type: isCurrentlySelected ? 'none' : 'electric',
      });

      return next;
    });
  };

  const handleReset = () => {

    setComparisonSelections(initialComparisonSelections);
    setElectricSelections(new Set());
  };

  // Blur button after touch to prevent stuck hover/focus states on mobile
  const handleTouchEnd = (e) => {
    e.currentTarget.blur();
  };

  const handleEmailClick = () => {
    // Validate before opening email modal

    if (!validationState.hasAnySelection) {

      setValidationMessage('Please select at least one appliance before emailing your results.');
      return;
    }

    setIsEmailModalOpen(true);
  };

  const hasSelections = validationState.hasAnySelection;

  return (
    <div className="interactive-home-tool">
      <div className="iht-container">
        {/* Left Side - Input Sections */}
        <div className="iht-input-side">
          {/* Section 1: Compare Appliances */}
          <div className="iht-section iht-section-compare">
            <div className="iht-section-header">
              <span className="iht-section-number">1</span>
              <span className="iht-section-title">
                Compare{' '}
                <span className="iht-nowrap">
                  <img src={electricIconSelected} alt="Electric Icon" className="iht-inline-icon" />
                  Electric
                </span>{' '}
                and{' '}
                <span className="iht-nowrap">
                  <img src={propaneIconSelected} alt="Propane Icon" className="iht-inline-icon" />
                  Propane
                </span>{' '}
                Appliances
              </span>
            </div>

            <div className="iht-compare-grid">
              {comparisonAppliancesList.map(({ key, labelLine1, labelLine2 }) => {
                const selection = comparisonSelections[key];
                const isElectricSelected = selection === 'electric';
                const isPropaneSelected = selection === 'propane';

                return (
                  <div key={key} className={`iht-compare-card ${selection ? 'selected' : ''}`}>
                    <div className="iht-compare-label">
                      <span>{labelLine1}</span>
                      <span>{labelLine2}</span>
                    </div>
                    <div className="iht-compare-buttons">
                      <button
                        id={`iht-compare-${key}-electric`}
                        className={`iht-fuel-btn ${isElectricSelected ? 'selected' : ''}`}
                        onClick={() => handleComparisonSelect(key, 'electric')}
                        onTouchEnd={handleTouchEnd}
                        aria-label={`Select electric ${labelLine1} ${labelLine2}`}
                      >
                        <img
                          src={isElectricSelected ? electricIconSelected : electricIconDeselected}
                          alt="Electric"
                        />
                      </button>
                      <span className="iht-or-text">or</span>
                      <button
                        id={`iht-compare-${key}-propane`}
                        className={`iht-fuel-btn ${isPropaneSelected ? 'selected' : ''}`}
                        onClick={() => handleComparisonSelect(key, 'propane')}
                        onTouchEnd={handleTouchEnd}
                        aria-label={`Select propane ${labelLine1} ${labelLine2}`}
                      >
                        <img
                          src={isPropaneSelected ? propaneIconSelected : propaneIconDeselected}
                          alt="Propane"
                        />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Section 2: Select Electric Appliances */}
          <div className="iht-section iht-section-electric">
            <div className="iht-section-header">
              <span className="iht-section-number">2</span>
              <span className="iht-section-title">Select Other Electric Appliances</span>
            </div>

            <div className="iht-electric-grid">
              {electricAppliancesList.map(({ id, label }) => {
                const isSelected = electricSelections.has(id);

                return (
                  <button
                    id={`iht-electric-${id}`}
                    key={id}
                    className={`iht-electric-btn ${isSelected ? 'selected' : ''}`}
                    onClick={() => handleElectricToggle(id)}
                    onTouchEnd={handleTouchEnd}
                    aria-pressed={isSelected}
                  >
                    <span>{label.toUpperCase()}</span>
                    <img
                      src={isSelected ? electricIconSelected : electricIconDeselected}
                      alt=""
                    />
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* Right Side - Output Section */}
        <div className="iht-output-side">
          <div className="iht-output-wrapper">
            {/* Decorative elements */}
            <img src={cloudIcon} alt="" className="iht-cloud iht-cloud-left" />
            <img src={cloudIcon} alt="" className="iht-cloud iht-cloud-right" />
            <img src={sunIcon} alt="" className="iht-sun" />
            <img src={groundIcon} alt="" className="iht-ground" />

            {/* House with results */}
            <div className="iht-house-container">
              <img src={houseIcon} alt="" className="iht-house-bg" />
              <div className="iht-house-content">
                <div className="iht-output-heading">Your Generator Size Is</div>
                <div className="iht-output-value">
                  <span className="iht-kw-number">
                    {hasSelections ? propaneKw.toFixed(2) : '0'}
                  </span>
                  <span className="iht-kw-unit">kW</span>
                </div>
                <p className="iht-output-message">
                  {validationState.hasPropaneSelected ? (
                    <>
                      instead of <strong>{maxAllElectricKw.toFixed(2)}kW</strong>
                      <br />with all electric appliances.
                    </>
                  ) : (
                    <>
                      Switch to <strong>propane appliances</strong>
                      <br />to see savings instantly.
                    </>
                  )}
                </p>
                <button className="iht-reset-btn" onClick={handleReset}>
                  Reset Calculator ↻
                </button>
              </div>
            </div>

            {/* Email CTA Button - Outside the house */}
            <button className="iht-email-btn" onClick={handleEmailClick}>
              EMAIL YOUR RESULTS
            </button>
          </div>
        </div>
      </div>

      {/* Email Modal */}
      <EmailModal
        isOpen={isEmailModalOpen}
        onClose={() => setIsEmailModalOpen(false)}
        onSessionReset={handleReset}
        generatorKw={propaneKw}
        allElectricKw={maxAllElectricKw}
        appliances={{
          comparison: comparisonSelections,
          electric: Array.from(electricSelections),
        }}
      />

      {/* Validation Alert Modal */}
      {validationMessage && (
        <div className="iht-modal-overlay" onClick={() => setValidationMessage(null)}>
          <div className="iht-validation-modal" onClick={(e) => e.stopPropagation()}>
            <button className="iht-modal-close" onClick={() => setValidationMessage(null)}>
              &times;
            </button>
            <div className="iht-validation-message">
              <p>{validationMessage}</p>
              <button
                className="iht-validation-ok-btn"
                onClick={() => setValidationMessage(null)}
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InteractiveHomeTool;
